export const ManageUsersContent = {
  // Page title
  pageTitle: 'Manage Users',

  // Search
  search: {
    placeholder: 'Search users by name, email, or group',
  },

  // Role filter
  roleFilter: {
    label: 'Filter by Role',
    all: 'All',
    admin: 'Admin',
    fellow: 'Fellow',
    circleMember: 'Circle Member',
    circleAlumni: 'Circle Alumni',
    fellowAlumni: 'Fellow Alumni',
  },

  // User count
  userCount: {
    showing: (filtered: number, total: number) =>
      `Showing ${filtered} of ${total} ${filtered === 1 ? 'user' : 'users'}`,
  },

  // Bulk edit
  bulkEdit: {
    toggleOn: 'Edit',
    toggleOff: 'Done',
    selectionCount: (count: number) =>
      `${count} ${count === 1 ? 'user' : 'users'} selected`,
    selectionContext: (selected: number, visible: number) =>
      selected > 0
        ? `${selected} selected · ${visible} visible`
        : `${visible} ${visible === 1 ? 'user' : 'users'} visible`,
    selectAll: 'Select all',
    selectAllCount: (count: number) => `Select all ${count}`,
    clearSelection: 'Clear',
    rolePlaceholder: 'Select role',
    applyRole: 'Apply Role',
    changeRole: 'Change role',
    addGroups: 'Add groups',
    deleteUsers: 'Delete Users',
    toast: {
      successTitle: 'Roles updated',
      successDescription: (count: number, role: string) =>
        `Updated ${count} ${count === 1 ? 'user' : 'users'} to ${role}.`,
      errorTitle: 'Update failed',
    },
    confirmRole: {
      title: 'Update roles?',
      description: (count: number, role: string) =>
        `Update ${count} ${count === 1 ? 'user' : 'users'} to ${role}.`,
      confirm: 'Update Roles',
      cancel: 'Cancel',
      loading: 'Updating...',
    },
    confirmDelete: {
      title: 'Delete users?',
      description: (count: number) =>
        `Delete ${count} ${count === 1 ? 'user' : 'users'}? This action cannot be undone.`,
      confirm: 'Delete Users',
      cancel: 'Cancel',
      loading: 'Deleting...',
      successTitle: 'Users deleted',
      successDescription: (count: number) =>
        `Deleted ${count} ${count === 1 ? 'user' : 'users'}.`,
      errorTitle: 'Delete failed',
    },
    roleDrawer: {
      title: 'Change role',
      description: (count: number) =>
        `Applies to ${count} selected ${count === 1 ? 'person' : 'people'}. Role controls app permissions. Groups stay as they are.`,
      review: 'Review change',
      cancel: 'Cancel',
      confirmTitle: 'Confirm role change',
      confirmSummary: (count: number) =>
        `Set ${count} ${count === 1 ? 'person' : 'people'} to`,
      willChange: (count: number, role: string) =>
        `${count} ${count === 1 ? 'person changes' : 'people change'} to ${role}.`,
      alreadyRole: (count: number, role: string) =>
        `${count} already ${role} - no change.`,
      permissionsWarning:
        'Permissions update immediately. Group memberships are untouched.',
      confirm: (count: number) =>
        `Change ${count} ${count === 1 ? 'person' : 'people'}`,
      loading: 'Changing...',
      back: 'Back',
      roleDescriptions: {
        admin: 'Full management access.',
        fellow: 'Standard fellowship program access.',
        circle_member: 'Circle programming and events.',
        circle_alumni: 'Circle alumni access.',
        fellow_alumni: 'Fellow alumni access.',
      },
    },
    addGroupsDrawer: {
      title: 'Add groups',
      selectedUsers: (count: number) =>
        `${count} ${count === 1 ? 'user' : 'users'} selected`,
      description: 'Existing memberships are preserved.',
      additiveOnly:
        "Additive only. This won't remove any groups the selected users already have.",
      searchPlaceholder: 'Search groups',
      empty: 'No active groups available.',
      searchEmpty: 'No active groups match this search.',
      alreadyHave: (count: number) =>
        `${count} already ${count === 1 ? 'has' : 'have'}`,
      allNew: 'all new',
      memberCount: (count: number) =>
        `${count} ${count === 1 ? 'member' : 'members'}`,
      willApply: 'Will apply',
      summary: (groupCount: number, userCount: number) =>
        `Add ${groupCount} ${groupCount === 1 ? 'group' : 'groups'} to ${userCount} ${userCount === 1 ? 'user' : 'users'}`,
      newMembershipSummary: (membershipCount: number, groupCount: number) =>
        `${membershipCount} new ${membershipCount === 1 ? 'membership' : 'memberships'} across ${groupCount} ${groupCount === 1 ? 'group' : 'groups'}.`,
      unchangedSummary: (count: number) =>
        `${count} existing ${count === 1 ? 'membership' : 'memberships'} unchanged.`,
      review: 'Review change',
      confirmTitle: 'Confirm group changes',
      confirmSummary: (userCount: number, groupCount: number) =>
        `Add ${userCount} ${userCount === 1 ? 'person' : 'people'} to ${groupCount} ${groupCount === 1 ? 'group' : 'groups'}.`,
      groupImpact: (added: number, unchanged: number) =>
        `${added} added${unchanged > 0 ? ` · ${unchanged} already in` : ''}`,
      newMemberships: 'New memberships',
      removed: 'Removed',
      back: 'Back',
      cancel: 'Cancel',
      confirm: 'Add to groups',
      loading: 'Adding...',
      successTitle: 'Groups added',
      successDescription: (added: number, unchanged: number) =>
        `${added} ${added === 1 ? 'membership' : 'memberships'} added. ${unchanged} existing ${unchanged === 1 ? 'membership was' : 'memberships were'} unchanged.`,
      errorTitle: 'Add groups failed',
    },
  },

  // Empty states
  emptyStates: {
    noUsers: 'No users found.',
    noUsersMatchFilter: 'No users match your search or filter.',
  },

  // Drawer labels
  drawer: {
    title: 'User Details',
    fullName: 'Full Name',
    email: 'Email',
    role: 'Role',
    memberSince: 'Member Since',
    permissions: {
      title: 'Permissions',
      canSeeAllEvents: {
        label: 'Can see all events?',
        description:
          'Allow this user to see all events regardless of their role.',
      },
    },
    actions: {
      changeRole: 'Change Role',
      deleteUser: 'Delete User',
      comingSoon: 'Coming Soon',
      selfModificationWarning: 'You cannot modify your own account',
    },
    roleChange: {
      title: 'Change Role',
      description: 'Select a new role for this user.',
      confirm: 'Update Role',
      cancel: 'Cancel',
      success: 'User role updated successfully',
      error: 'Failed to update user role',
    },
    delete: {
      confirmation: {
        title: 'Delete User',
        description:
          'Are you sure you want to delete this user? This action cannot be undone.',
        confirm: 'Delete User',
        cancel: 'Cancel',
      },
      success: 'User deleted successfully',
      error: 'Failed to delete user',
      selfDeleteError: 'You cannot delete your own account',
    },
  },

  // Group memberships
  groups: {
    sectionTitle: 'Groups',
    assignedCount: (n: number) =>
      `${n} ${n === 1 ? 'group' : 'groups'} assigned`,
    unassigned: 'Not assigned to any groups.',
    editAction: 'Edit Groups',
    rowEmpty: 'No groups',
    badgeOverflow: (n: number) => `+${n}`,
    popoverTitle: 'All groups',
    filter: {
      label: 'Groups',
      helper: 'OR · any selected',
      addGroup: 'Add group',
      showArchived: 'Show archived',
      hideArchived: 'Hide archived',
      searchPlaceholder: 'Search groups',
      empty: 'No groups available.',
      searchEmpty: 'No groups match this search.',
      clear: 'Clear group filters',
      countContext: 'in any selected group',
    },
    drawer: {
      title: 'Edit groups',
      description:
        "Groups are independent of role. Changing this user's role won't add or remove memberships.",
      searchPlaceholder: 'Search groups',
      showArchived: 'Show archived',
      archivedReadOnly: 'Archived · read-only',
      sectionCount: (assigned: number, total: number) =>
        `${assigned} of ${total}`,
      save: (changes: number) =>
        `Save (${changes} ${changes === 1 ? 'change' : 'changes'})`,
      saveNoChanges: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      empty: 'No groups available.',
      searchEmpty: 'No groups match this search.',
      loadError: 'Could not load groups.',
      successTitle: 'Groups updated',
      successDescription: (userName: string) =>
        `Updated groups for ${userName}.`,
      errorTitle: 'Update failed',
    },
  },

  // Invite section
  invite: {
    title: 'Add Users',
    description:
      'Send an invite to a new user. They will receive a magic link via email.',
  },

  // Bulk import section
  bulkImport: {
    title: 'Import Users',
    description: 'Upload a CSV file to create multiple user accounts.',
    addMultiple: 'Bulk Create',
    downloadTemplate: 'Download Template',
    selectFile: 'Select CSV File',
    noFileSelected: 'No file selected',
    importing: 'Importing...',
    importUsers: (count: number) =>
      `Import ${count} ${count === 1 ? 'User' : 'Users'}`,
    results: {
      success: (count: number) =>
        `${count} ${count === 1 ? 'user' : 'users'} created`,
      failed: (count: number) =>
        `${count} ${count === 1 ? 'user' : 'users'} failed`,
      title: 'Import Results',
    },
    errors: {
      invalidFile: 'Please select a valid CSV file',
      emptyFile: 'CSV file is empty',
      noEmailColumn: 'CSV must contain an "email" column',
      parseError: 'Failed to parse CSV file',
    },
  },
};
